import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { Env, VpnGatewayRecord, VpnPeerStatus } from '@fleet-cert-console/shared';
import { fetchVpnPeerStatus, getVpnGateway } from '../api/client.js';
import { getConfig } from '../config.js';
import { VpnConnectHelpModal } from '../components/VpnConnectHelpModal.js';
import { VpnFilePushModal } from '../components/VpnFilePushModal.js';
import { VpnGatewayDetail } from '../components/VpnGatewayDetail.js';
import { peersByKey } from '../lib/vpnPeers.js';
import { vpnStatusIndicator } from '../lib/vpnStatus.js';

const envFromParam = (value: string | null): Env => (value === 'prod' ? 'prod' : 'dev');

/** Statuses that mean the control plane is mid-flight — the page polls while the row is in one. */
const ACTIVE_STATUSES = new Set(['requested', 'provisioning', 'applying']);

const POLL_MS = 5000;

/**
 * Standalone page for one gateway's enrollment (/vpn/:gatewayId) — the split panel's content with
 * its own URL, so a gateway can be opened in a tab, bookmarked, or linked in chat. Read-and-connect
 * only: initiate/approve and link actions stay on the Gateway enrollment page.
 */
export function VpnGatewayDetailPage() {
  const navigate = useNavigate();
  const { gatewayId } = useParams();
  // Environment from the URL (?env=dev|prod), as on the enrollment page; default dev.
  const [searchParams] = useSearchParams();
  const env = envFromParam(searchParams.get('env'));

  // undefined = still loading, null = the control plane has no row for this id.
  const [gateway, setGateway] = useState<VpnGatewayRecord | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [filePushOpen, setFilePushOpen] = useState(false);
  // Live hub peer state — fetched on load and Refresh (an SSM round trip), never on the 5 s poll.
  const [peerStatus, setPeerStatus] = useState<{ byKey: Map<string, VpnPeerStatus>; fetchedAt: string; hubInstanceId?: string } | null>(null);

  const loadPeerStatus = useCallback(async () => {
    try {
      const res = await fetchVpnPeerStatus(env);
      setPeerStatus({ byKey: peersByKey(res), fetchedAt: res.fetchedAt, hubInstanceId: res.hubInstanceId });
    } catch {
      setPeerStatus(null); // non-fatal — the connectivity section simply doesn't render
    }
  }, [env]);

  const load = useCallback(async () => {
    if (!gatewayId) return;
    try {
      // getVpnGateway is deliberately uncached — every call (poll ticks included) is a live read.
      const g = await getVpnGateway(env, gatewayId);
      setGateway(g);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [gatewayId, env]);

  useEffect(() => {
    setGateway(undefined);
    setError(null);
    void load();
    void loadPeerStatus();
  }, [load, loadPeerStatus]);

  // Poll while the enrollment is mid-flight, same cadence as the list page.
  const active = ACTIVE_STATUSES.has(gateway?.vpnStatus ?? '');
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(timer);
  }, [active, load]);

  const status = gateway ? vpnStatusIndicator(gateway.vpnStatus) : null;

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={gateway?.hostname ? `${env} · ${gateway.hostname}` : env}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                iconName="upload"
                disabled={gateway?.vpnStatus !== 'applied'}
                onClick={() => setFilePushOpen(true)}
              >
                Send file…
              </Button>
              <Button
                iconName="external"
                disabled={gateway?.vpnStatus !== 'applied'}
                onClick={() =>
                  window.open(
                    `/vpn/${encodeURIComponent(gatewayId ?? '')}/terminal?env=${env}`,
                    // Unique per click: a reused window name renavigates (kills) a live session.
                    `vx-terminal-${gatewayId ?? ''}-${Date.now()}`,
                    'popup=yes,width=1100,height=700',
                  )
                }
              >
                Open terminal
              </Button>
              <Button iconName="status-info" onClick={() => setHelpOpen(true)}>
                Connection help
              </Button>
              <Button
                iconName="refresh"
                ariaLabel="Refresh"
                onClick={() => {
                  void load();
                  void loadPeerStatus();
                }}
              />
              <Button onClick={() => navigate(`/vpn?env=${env}`)}>Back to Gateway enrollment</Button>
            </SpaceBetween>
          }
        >
          {gatewayId}
        </Header>
      }
    >
      <SpaceBetween size="m">
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError(null)} header="Request failed">
            {error}
          </Alert>
        )}
        {gateway === undefined && !error && <Box padding="l">Loading…</Box>}
        {gateway === null && (
          <Alert type="warning" header="Gateway not found">
            Nothing named <code>{gatewayId}</code> in {env}: no VPN enrollment row and no provisioned
            gateway Thing (<code>provisioningStatus = COMPLETE</code>). Once the gateway is provisioned it
            appears on the Gateway enrollment page, where it can be enrolled.
          </Alert>
        )}
        {gateway && status && (
          <Container
            header={
              <Header variant="h2" actions={<StatusIndicator type={status.type}>{status.label}</StatusIndicator>}>
                Enrollment
              </Header>
            }
          >
            <VpnGatewayDetail
              gateway={gateway}
              hubInstanceId={peerStatus?.hubInstanceId ?? getConfig().vpnHubInstanceId}
              peer={gateway.gatewayPubKey ? peerStatus?.byKey.get(gateway.gatewayPubKey) : undefined}
              peerFetchedAt={peerStatus?.fetchedAt}
            />
          </Container>
        )}
      </SpaceBetween>
      <VpnConnectHelpModal
        visible={helpOpen}
        onDismiss={() => setHelpOpen(false)}
        vpnIp={gateway?.vpnIp}
        hubInstanceId={peerStatus?.hubInstanceId ?? getConfig().vpnHubInstanceId}
      />
      <VpnFilePushModal
        visible={filePushOpen}
        env={env}
        gatewayId={gatewayId ?? ''}
        onDismiss={() => setFilePushOpen(false)}
      />
    </ContentLayout>
  );
}
